/**
 * ModelPicker — reusable radio-card picker for AI model selection.
 * Reads catalog from Zustand store, falls back to FALLBACK_CATALOG.
 * compact mode: renders inside a DropdownPicker popover.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAiStore } from '@/store/aiStore.ts';
import { FALLBACK_CATALOG } from '@/ai/tokenCosts.ts';
import { DropdownPicker } from './DropdownPicker.tsx';
import type { ModelCategory } from '@/types/ai.ts';
import styles from './ModelPicker.module.css';

interface Props {
  category: ModelCategory;
  selectedModelId: string;
  onSelect: (modelId: string) => void;
  compact?: boolean;
}

export function ModelPicker({ category, selectedModelId, onSelect, compact }: Props) {
  const { t } = useTranslation('aiWorkspace');
  const catalog = useAiStore(s => s.aiModelCatalog);
  const effective = catalog.length > 0 ? catalog : FALLBACK_CATALOG;
  const models = effective
    .filter(m => m.category === category && m.id !== 'flux2-flash' && m.id !== 'kling-v3')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = useCallback(() => setDropdownOpen(v => !v), []);

  if (models.length === 0) return null;

  // Find the slowest model in category for relative speed bar
  const maxSeconds = Math.max(...models.map(m => m.estSeconds));

  const renderCards = () => models.map(m => {
    const selected = m.id === selectedModelId;
    const speedPct = maxSeconds > 0 ? Math.round(((maxSeconds - m.estSeconds) / maxSeconds) * 100 + 20) : 50;

    return (
      <button
        key={m.id}
        className={`${styles.card} ${selected ? styles.cardSelected : ''}`}
        onClick={() => {
          onSelect(m.id);
          if (compact) setDropdownOpen(false);
        }}
        type="button"
      >
        <div className={styles.cardHeader}>
          <span className={styles.cardName}>{m.name}</span>
          <span className={styles.cardCost}>{m.costPerSecond != null ? `${m.costPerSecond}${t('creditSuffix')}/s` : `${m.cost} ${t('creditSuffix')}`}</span>
        </div>
        <div className={styles.speedBar}>
          <div className={styles.speedFill} style={{ width: `${Math.min(100, speedPct)}%` }} />
        </div>
        <div className={styles.cardMeta}>
          <span>~{m.estSeconds}s</span>
          {m.resolution && <span className={styles.badge}>{m.resolution}</span>}
        </div>
      </button>
    );
  });

  if (compact) {
    const selected = models.find(m => m.id === selectedModelId);
    return (
      <DropdownPicker
        selectedLabel={selected?.name ?? t('selectModel')}
        selectedSublabel={selected ? (selected.costPerSecond != null ? `${selected.costPerSecond}${t('creditSuffix')}/s` : `${selected.cost} ${t('creditSuffix')}`) : undefined}
        open={dropdownOpen}
        onToggle={toggleDropdown}
      >
        <div className={styles.pickerCompact}>
          {renderCards()}
        </div>
      </DropdownPicker>
    );
  }

  return (
    <div className={styles.picker}>
      {renderCards()}
    </div>
  );
}
